import { Injectable, OnDestroy } from "@angular/core";
import { MatLegacyDialogRef as MatDialogRef } from "@angular/material/legacy-dialog";
import { Store } from "@ngrx/store";
import { AlertButton, AlertComponent } from "@obg/mat.ui.core";
import { AbstractTranslatableAlertService, CORE_ROUTE, CoreState, Jurisdiction } from "@obg/ngx.core";
import { TranslationService } from "@obg/ngx.core.content";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { Subscription } from "rxjs";
import { tap } from "rxjs/operators";

import { SmTermsAndConditionsMessage } from "../alert-message/sm-terms-and-conditions-message.container";

import { UpdatedTermsAndConditionsConfigService } from "./updated-terms-and-conditions.config";
import { UpdatedTermsAndConditionsService } from "./updated-terms-and-conditions.service";

export const UPDATED_TC_ALERT_ID = "UPDATED_TC_ALERT";

@Injectable({
	providedIn: "root",
})
export class UpdatedTermsAndConditionsAlertService implements OnDestroy {
	private matDialogRef?: MatDialogRef<AlertComponent, any>;
	private alert$$?: Subscription;
	private routeChange$$?: Subscription;

	constructor(
		private store: Store<CoreState>,
		private alertService: AbstractTranslatableAlertService,
		private translationService: TranslationService,
		private service: UpdatedTermsAndConditionsService,
		private config: UpdatedTermsAndConditionsConfigService
	) {}

	open(jurisdiction: Jurisdiction) {
		const isExtendedUi = this.config.isExtended();

		this.routeChange$$ = this.store.select(RouteSelector.getCurrent).subscribe(() => {
			if (this.matDialogRef) {
				this.matDialogRef.close();
				this.routeChange$$?.unsubscribe();
			}
		});

		this.alert$$ = this.alertService
			.open$(
				{
					id: UPDATED_TC_ALERT_ID,
					messageComponent: SmTermsAndConditionsMessage,
					messageComponentInit: (component: SmTermsAndConditionsMessage) =>
						(component.data = {
							title: this.service.getTitle(),
							message: this.service.getMessage(jurisdiction),
							summary: this.service.getSummary(jurisdiction),
							isExtendedUi,
						}),
					buttons: this.getAlertButtons(isExtendedUi),
					dialogConfig: {
						maxHeight: "75vh",
						closeOnNavigation: true,
						disableClose: true,
					},
					mustBeInteracted: true,
				},
				[]
			)
			.pipe(tap((matDialogRef) => (this.matDialogRef = matDialogRef)))
			.subscribe();
	}

	getAlertButtons(isExtendedUi: boolean): AlertButton[] {
		const readMoreButton = {
			text: this.translationService.getByKey("terms-and-conditions.updated-read"),
			handler: () => this.store.dispatch(RouteAction.navigate(CORE_ROUTE.termsAndConditions)),
			testIdSuffix: "read-more",
		};

		const acceptButton = {
			text: this.translationService.getByKey("terms-and-conditions.updated-accept"),
			handler: () => {
				this.service.acceptTermsAndConditions();
			},
			testIdSuffix: "accept",
		};

		const declineButton = {
			text: this.translationService.getByKey("terms-and-conditions.m-updated-decline"),
			handler: () => {
				this.service.setDeclineWarningState(true);
				this.store.dispatch(RouteAction.navigate(CORE_ROUTE.termsAndConditions));
			},
			testIdSuffix: "decline",
		};

		if (isExtendedUi) {
			return [declineButton, acceptButton];
		}

		return [readMoreButton, acceptButton];
	}

	ngOnDestroy() {
		this.routeChange$$?.unsubscribe();
		this.alert$$?.unsubscribe();
	}
}
